import React, { useEffect, useState } from "react"; // useState kept for DonutChart
import BeautyButton from '../../components/BeautyButton';

// Animated Donut Chart
const DonutChart = ({ targetValue }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    let current = 0;
    const step = targetValue / 60;
    const interval = setInterval(() => {
      current += step;
      if (current >= targetValue) {
        current = targetValue;
        clearInterval(interval);
      }
      setValue(Math.round(current));
    }, 16);
    return () => clearInterval(interval);
  }, [targetValue]);

  return (
    <div className="relative w-48 h-48 mx-auto"> {/* was w-32 h-32 */}
      <svg className="w-full h-full overflow-visible" viewBox="0 0 36 36">
        {/* Background */}
        <path
          className="text-[#C58348]"
          strokeWidth="5" 
          fill="none"
          stroke="currentColor"
          d="M18 2.0845
             a 15.9155 15.9155 0 0 1 0 31.831
             a 15.9155 15.9155 0 0 1 0 -31.831"
        />
        {/* Progress */}
        <path
          className="text-[#5F40A1]"
          strokeWidth="5"
          fill="none"
          stroke="currentColor"
          strokeDasharray={`${value}, 100`}
          d="M18 2.0845
             a 15.9155 15.9155 0 0 1 0 31.831
             a 15.9155 15.9155 0 0 1 0 -31.831"
        />
      </svg>
      <div className="text-[#5F40A1] absolute inset-0 flex items-center justify-center text-2xl font-bold">
        {value}%
      </div>
    </div>
  );
};



const normalizeIngredient = (str) =>
  str.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

const AntiAgeSummary = ({ addedProducts = [] }) => {

  const totalIngredients = [
    "Broad-spectrum sunscreen (SPF 30+)",
    "Tretinoin (all-trans retinoic acid)",
    "Tazarotene",
    "Adapalene",
    "Retinol",
    "Retinaldehyde (retinal)",
    "L-ascorbic acid (Vitamin C)",
    "Vitamin E (alpha-tocopherol)",
    "Ferulic acid",
    "Niacinamide (Vitamin B3)",
    "Glycolic acid",
    "Lactic acid",
    "Polyhydroxy acids (PHAs)",
    "Salicylic acid (BHA)",
    "Azelaic acid",
    "Tranexamic acid",
    "Hydroquinone",
    "Hyaluronic acid",
    "Ceramides",
    "Bakuchiol",
    "Palmitoyl pentapeptide-4 / tripeptide-1/7",
    "Copper peptide (GHK-Cu)",
    "Adenosine",
    "Green tea polyphenols (EGCG)",
    "Resveratrol"
  ];
  

  // Collect all ingredient names from added products
  const allApiIngredients = new Set(
    addedProducts.flatMap(p => {
      const names = typeof p.ingredient_names === 'string'
        ? p.ingredient_names.replace(/^\{|\}$/g, '').split(',')
        : (p.ingredient_names ?? []);
      return names.map(n => normalizeIngredient(n.trim()));
    })
  );

  // Match totalIngredients against added product ingredients
  const matchedSet = new Set(
    totalIngredients.filter(ingredient => {
      const norm = normalizeIngredient(ingredient);
      return [...allApiIngredients].some(api => norm.includes(api) || api.includes(norm));
    })
  );

  const usedPercent = totalIngredients.length > 0
    ? Math.round((matchedSet.size / totalIngredients.length) * 100)
    : 0;

  const usedCount = matchedSet.size;

  return (
    <div className="w-full h-auto flex flex-col md:flex-row items-center justify-center bg-[#E9A969] shadow-md rounded-lg p-8 gap-6">
    {/* Left: Chart & description */}
    <div className="flex flex-col items-center justify-center md:w-1/3 w-full mb-4 md:mb-0">
      <DonutChart targetValue={usedPercent} />
    </div>
  
    {/* Middle: Skincare Coverage */}
    
    <div className="md:w-1/5 w-full mb-4 text-center md:text-left">
    <BeautyButton
      text="coverage"
      description={
        'This chart shows how much of the most effective, research-backed anti-aging skincare you’re currently using. Each slice represents a proven ingredient—from sunscreen and retinoids to antioxidants and peptides. The percentage reflects your routine’s coverage, helping you see both your strengths and the opportunities to add high-impact ingredients for even better results.'
           }
      type="popup"
    />
      {/* <h3 className="text-[#2D1735] text-lg font-semibold mb-2">skincare coverage</h3> */}
      <p className="mt-2 text-[#2D1735]/80">
      <br/>
        You’re using <strong>{usedCount}</strong> of the possible anti-aging ingredients in your everyday skincare.
        <br/>Some key ingredients are missing, so there’s room to optimize your routine!
      </p>
    </div>
  
    {/* Right: Ingredient list */}
    <div className="md:w-2/3 w-full">
      {/* <h3 className=" text-[#2D1735] font-semibold mb-4 text-xl text-center md:text-left">Top 20 Anti-Aging Ingredients</h3> */}
      <ul className="flex flex-wrap gap-2 justify-center md:justify-start">
        {totalIngredients.map((ingredient, index) => {
          const isUsed = matchedSet.has(ingredient);
          return (
            <li
              key={index}
              className={`px-4 py-2 rounded-full text-sm font-medium border shadow-sm transition-colors duration-300 ${
                isUsed
                  ? "bg-[#CFC7DF]/40 border-[#5F40A1]/80 text-[#5F40A1]"
                  : "bg-[#C58348]/50 border-[#C58348]/100 text-[#C58348]"
              }`}
            >
              {ingredient}
            </li>
          );
        })}
      </ul>
    </div>
  </div>
  
);

};

export default AntiAgeSummary;
